import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { AbstractDialogComponent } from '../../../../Core/Dialogs/components/AbstractDialog/abstract-dialog.component';
import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { ProgressModel, ProgressStageModel } from '../../models/progress.model';
import { ButtonTypesEnum } from '../../../Forms/enums/button-types.enum';
import { messageSeverityEnum } from '../../../Misc/enums/message-severity.enum';


@Component({
    selector: 'agpr-progress-dialog',
    templateUrl: './progress-dialog.component.html',
    styleUrls: ['./progress-dialog.component.scss'],
    standalone: false
})
export class ProgressDialogComponent extends AbstractDialogComponent implements OnInit, OnDestroy {
    @Input() public id: string;
    @Input() public headerLabel: string;
    @Input() public keywordLabel: string;
    @Input() public cancelButtonLabel: string = 'Monitoring.cancel';
    @Input() public progress: ProgressModel;
    @Input() public progress$: Observable<ProgressModel>;
    @Input() public showCount: boolean = true;
    @Input() public showCountBottom: boolean = false;
    @Input() public showPercentage: boolean = false;
    @Input() alertMessage: string;
    @Input() alertType: messageSeverityEnum;
    @Input() generatingText: string = '';

    /** When provided alongside estimatedDurationMs, the dialog drives fake progress internally
     *  and auto-completes when the observable resolves. */
    @Input() work$: Observable<unknown>;
    /** Exponential time-constant in ms for the fake progress curve. */
    @Input() estimatedDurationMs: number;

    /** Optional sequence of stages whose messages replace generatingText while work$ runs. */
    @Input() set progressStages(stages: ProgressStageModel[] | undefined) {
        this._progressStages = stages;
        if (stages?.length) {
            this.startStageTimer(stages);
        }
    }
    get progressStages(): ProgressStageModel[] | undefined {
        return this._progressStages;
    }

    readonly buttonTypes = ButtonTypesEnum;

    activeStageText: string = '';

    private _progressStages?: ProgressStageModel[];
    private stageSchedule: { text: string; displayAt: number }[] = [];
    private workCompletedWithStages = false;
    private readonly startTime = Date.now();
    private subscriptions: Subscription[] = [];
    private progressTimer: ReturnType<typeof setInterval>;
    private stageTimer: ReturnType<typeof setInterval>;
    private workSubscription: Subscription;

    constructor(coreDialogService: CoreDialogService) {
        super(coreDialogService, 'Progress');
    }

    ngOnInit(): void {
        let progress$: Observable<ProgressModel>;

        if (this.work$ && this.estimatedDurationMs) {
            const totalItems = 1000;
            const maxProgress = 880 + Math.floor(Math.random() * 70);
            const progressSubject = new BehaviorSubject<ProgressModel>({ currentIndex: 0, totalItems });
            progress$ = progressSubject.asObservable();

            this.progressTimer = setInterval(() => {
                const elapsed = Date.now() - this.startTime;
                const current = Math.floor(maxProgress * (1 - Math.exp(-elapsed / this.estimatedDurationMs)));
                progressSubject.next({ currentIndex: current, totalItems });
            }, 100);

            this.workSubscription = this.work$.subscribe({
                next: () => {
                    clearInterval(this.progressTimer);
                    if (this.stageSchedule.length) {
                        clearInterval(this.stageTimer);
                        this.activeStageText = this.stageSchedule[this.stageSchedule.length - 1].text;
                        this.workCompletedWithStages = true;
                        progressSubject.next({ currentIndex: totalItems, totalItems });
                        setTimeout(() => this.closeDialog(true), 1500);
                    } else {
                        progressSubject.next({ currentIndex: totalItems, totalItems });
                    }
                },
                error: () => {
                    clearInterval(this.progressTimer);
                    progressSubject.complete();
                    this.closeDialog(false);
                }
            });
        } else {
            progress$ = this.progress$;
        }

        if (!this._progressStages?.length) {
            this.activeStageText = this.generatingText;
        }

        if (progress$) {
            this.subscriptions.push(
                progress$.subscribe(progress => {
                    this.progress = progress;
                    if (this.progress.currentIndex === this.progress.totalItems && !this.workCompletedWithStages) {
                        setTimeout(() => {
                            this.closeDialog(true);
                        }, 2000);
                    }
                })
            );
        }
    }

    onCancelClick() {
        this.closeDialog(false);
    }

    calcPercentComplete(): number {
        return ((this.progress?.currentIndex / this.progress?.totalItems) * 100);
    }

    ngOnDestroy() {
        clearInterval(this.progressTimer);
        clearInterval(this.stageTimer);
        this.workSubscription?.unsubscribe();
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }

    private startStageTimer(stages: ProgressStageModel[]): void {
        clearInterval(this.stageTimer);
        const schedule = this.buildStageSchedule(stages);
        this.stageSchedule = schedule;
        this.activeStageText = schedule[0]?.text ?? '';
        this.stageTimer = setInterval(() => {
            const elapsed = Date.now() - this.startTime;
            let active = schedule[0]?.text ?? '';
            for (const entry of schedule) {
                if (entry.displayAt <= elapsed) {
                    active = entry.text;
                } else {
                    break;
                }
            }
            this.activeStageText = active;
        }, 250);
    }

    private buildStageSchedule(stages: ProgressStageModel[]): { text: string; displayAt: number }[] {
        const schedule: { text: string; displayAt: number }[] = [];
        let time = 0;
        for (const stage of stages) {
            const messages = stage.mayLoop ? this.expandLoopedMessages(stage.messages) : stage.messages;
            for (const msg of messages) {
                schedule.push({ text: msg, displayAt: time });
                const jitter = (Math.random() * 0.6 - 0.3); // +/-30%
                time += Math.round(stage.approximateDurationMsPerMessage * (1 + jitter));
            }
        }
        return schedule;
    }

    private expandLoopedMessages(messages: string[]): string[] {
        const loopCount = 1 + Math.floor(Math.random() * 3);
        const result: string[] = [];
        for (let i = 0; i < loopCount; i++) {
            result.push(...messages);
        }
        return result;
    }
}
